import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import Fieldset from '../../Common/Components/Fieldset'
import FormRow from '../../Common/Components/FormRow'
import Heading from '../../Common/Components/Heading'
import { Select, SelectOptions, SelectRangeItem } from '../../Common/Components/Selects'
import TextInput from '../../Common/Components/TextInput'

import { LanguageContext } from '../../Common/Contexts'

import { LocationalFiltering as LocationalFilteringLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { AgentResponse } from '../../../NonReact/Types/Types'
import { IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
import {
    ALL_VALUE_STRING_DEFAULT,
    ANY_VALUE_PARTITION_GUID,
    ANY_VALUE_STRING_DEFAULT
} from '../../Common/Constants/DefaultValues'
import AdvancedModeContext, { AdvancedModeContextType } from '../../Common/Contexts/AdvancedModeContext'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import {
    GenericFilterEntityDataItems,
    GenericFilterEntityItem,
    GroupMembershipResponse,
    RptFilterEntityInfo
} from '../../Common/Types/Types'
import { FILTER_LOCATION_ANY, FILTER_LOCATION_FROM, FILTER_LOCATION_TO, MAX_DEVICES_AGENTS } from './Constants'
import { DeviceResponse } from './Types'
import { HelpTag } from '../../Common/Components/HelpTag/HelpTag'
import { HelpTagTitles, Tags } from '../../Common/API/Contentful/Constants'
import { ReportTypes } from '../../../NonReact/constants/ReportTypes'
import { RangeCombo } from '../../Common/Components/Selects/RangeCombo/RangeCombo'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { SelectOption } from '../../Common/Components/Selects/Types'
import { SearchableEntities, searchEntitiesAcrossPartitions } from '../../Common/API/SearchingEntityAPIClient'
import { getAgents, getDevices, getGroupMembers } from '../../../NonReact/common/API/EntityAPIClient'
import { ArrayUtils } from '../../../NonReact/utils/ArrayUtils'
import { reduceGroupMemberships } from '../../../NonReact/common/API/APIMappers'

/**
 * Component properties type.
 */
type Props = {
    filterEntityInfo: RptFilterEntityInfo | undefined
    telephoneNos: string
    setTelephoneNos: (newValue: string) => void
    telephoneNoDescriptions: string
    setTelephoneNoDescriptions: (newValue: string) => void
    devices: string
    setDevices: (newValue: string) => void
    ACDAgents: string
    setACDAgents: (newValue: string) => void
    filterLocationToFrom: number
    setFilterLocationToFrom: (newValue: number) => void
    telSys: number
    partitions: string[]
    reportType?: ReportTypes
    currentTelSysType?: number
    selectedPartitionsPartIDOnTelSys: Nullable<number[]>
    deviceTypes: number[]
    availableDevices: SelectRangeItem[]
    setAvailableDevices: (devices: SelectRangeItem[]) => void
    scopeGroups: string[]
}

export const DEVICE_CACHE_TIME: number = 10000

/**
 * Locational filtering section in filter tab.
 * @param filterEntityInfo The list of entities according to the filter.
 * @param telephoneNos Report property.
 * @param telephoneNoDescriptions Report property.
 * @param setTelephoneNos Setter for corresponding report property.
 * @param setTelephoneNoDescriptions Setter for corresponding report property.
 * @param devices Report property.
 * @param setDevices Setter for corresponding report property.
 * @param ACDAgents Report property.
 * @param setACDAgents Setter for corresponding report property.
 * @param filterLocationToFrom Report property.
 * @param setFilterLocationToFrom Setter for corresponding report property.
 * @param telSys Report property.
 * @param partitions Report property.
 * @param reportType Report property.
 * @param currentTelSysType Report property.
 * @param selectedPartitionsPartIDOnTelSys Selected partitions IDs on the telephone system.
 * @param deviceTypes Device types to filter by.
 * @param availableDevices Available devices to select from.
 * @param setAvailableDevices Setter for available devices.
 * @param scopeGroups Selected scope groups for filtering.
 *
 */
const LocationalFiltering: React.FC<Props> = ({
    filterEntityInfo,
    telephoneNos,
    setTelephoneNos,
    telephoneNoDescriptions,
    setTelephoneNoDescriptions,
    devices,
    setDevices,
    ACDAgents,
    setACDAgents,
    filterLocationToFrom,
    setFilterLocationToFrom,
    telSys,
    partitions,
    reportType,
    currentTelSysType,
    selectedPartitionsPartIDOnTelSys,
    deviceTypes,
    availableDevices,
    setAvailableDevices,
    scopeGroups
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('LocationalFiltering')

    const ANY_LANGUAGE: string = language.get(LocationalFilteringLanguage.LocationalFiltering_AnyPlaceholder)

    const standardAny: SelectRangeItem = {
        value: ANY_LANGUAGE,
        description: ANY_LANGUAGE,
        renderElement: <div>{ANY_LANGUAGE}</div>
    }

    const { isAdvancedMode }: AdvancedModeContextType = useContext(AdvancedModeContext)
    const { displayError }: MessageContextType = useContext(MessageContext)

    const locationSelectOptions: SelectOptions<number> = [
        {
            value: FILTER_LOCATION_ANY,
            label: language.get(LocationalFilteringLanguage.LocationOptionAny)
        },
        {
            value: FILTER_LOCATION_FROM,
            label: language.get(LocationalFilteringLanguage.LocationOptionFrom)
        },
        {
            value: FILTER_LOCATION_TO,
            label: language.get(LocationalFilteringLanguage.LocationOptionTo)
        }
    ]

    const [availableAgents, setAvailableAgents] = useState<SelectRangeItem[]>([])

    const toEntityOptions = (entityContainerItem: GenericFilterEntityItem | undefined): SelectRangeItem[] =>
        entityContainerItem
            ? entityContainerItem.Data.map((entity) => ({
                  value: IsVarEmpty(entity.EncodedID) ? entity.ID : entity.EncodedID,
                  description: entity.Description,
                  renderElement: (
                      <div>
                          {entity.ID} - {entity.Description}
                      </div>
                  )
              }))
            : []

    const filterDevicesByDeviceTypes: Definable<GenericFilterEntityItem> =
        useMemo((): Definable<GenericFilterEntityItem> => {
            if (deviceTypes.length === 1 && deviceTypes[0] === ALL_VALUE) return filterEntityInfo?.EntityInfo.Devices

            return {
                Data: [
                    ...(filterEntityInfo?.EntityInfo.Devices.Data.filter(
                        (device: GenericFilterEntityDataItems): boolean =>
                            device.ID === ANY_VALUE_STRING_DEFAULT || deviceTypes.includes(device?.Type ?? 0)
                    ) ?? [])
                ]
            }
        }, [deviceTypes, JSON.stringify(filterEntityInfo?.EntityInfo.Devices)])

    useEffect((): void => {
        if (
            telSys &&
            partitions &&
            telSys !== ALL_VALUE &&
            partitions.length > 0 &&
            partitions[0] !== ANY_VALUE_PARTITION_GUID &&
            (selectedPartitionsPartIDOnTelSys?.length ?? 0) > 0
        ) {
            if (selectedPartitionsPartIDOnTelSys?.length === 0) return

            Promise.all(
                selectedPartitionsPartIDOnTelSys?.map(
                    (partInTelSysID: Definable<number>): Definable<Promise<Voidable<DeviceResponse[]>>> => {
                        if (partInTelSysID === undefined) return undefined

                        const deviceTypeFilters: Definable<string[]> =
                            deviceTypes.length === 1 && deviceTypes[0] === ALL_VALUE
                                ? undefined
                                : deviceTypes.map((filter: number): string => `${filter}`)

                        if (!ArrayUtils.isEmpty(scopeGroups) && scopeGroups?.[0] !== ALL_VALUE_STRING_DEFAULT) {
                            return Promise.all(
                                scopeGroups.map(
                                    (scopeGroup: string): Promise<Nullable<GroupMembershipResponse>> =>
                                        getGroupMembers(
                                            telSys,
                                            partInTelSysID,
                                            scopeGroup,
                                            undefined,
                                            undefined,
                                            undefined,
                                            DEVICE_CACHE_TIME
                                        )
                                )
                            )
                                .then(reduceGroupMemberships)
                                .then(
                                    (groupMembers: Nullable<GroupMembershipResponse>): DeviceResponse[] =>
                                        (groupMembers?.Devices as DeviceResponse[]) ?? ([] as DeviceResponse[])
                                )
                                .catch(displayError)
                        }

                        return getDevices(
                            telSys,
                            partInTelSysID,
                            true,
                            deviceTypeFilters,
                            undefined,
                            DEVICE_CACHE_TIME,
                            undefined,
                            true
                        ).catch(displayError)
                    }
                ) ?? []
            ).then((devicesArrays: Voidable<DeviceResponse[]>[]): void => {
                setAvailableDevices(
                    [
                        standardAny,
                        ...(
                            devicesArrays.filter(
                                (devicesArray: Voidable<DeviceResponse[]>): boolean => !IsVarEmpty(devicesArray)
                            ) as DeviceResponse[][]
                        )
                            .reduce(
                                (accum: DeviceResponse[], next: DeviceResponse[]): DeviceResponse[] => [
                                    ...accum,
                                    ...next
                                ],
                                []
                            )
                            .map(
                                ({ DeviceNumber, Description }: DeviceResponse): SelectRangeItem => ({
                                    value: DeviceNumber,
                                    description: Description,
                                    renderElement: (
                                        <div>
                                            {DeviceNumber} - {Description}
                                        </div>
                                    )
                                })
                            )
                    ].slice(0, MAX_DEVICES_AGENTS)
                )
            })

            Promise.all(
                selectedPartitionsPartIDOnTelSys?.map(
                    (partInTelSysID: Definable<number>): Definable<Promise<Voidable<AgentResponse[]>>> => {
                        if (partInTelSysID === undefined) return undefined

                        if (!ArrayUtils.isEmpty(scopeGroups) && scopeGroups?.[0] !== ALL_VALUE_STRING_DEFAULT) {
                            return Promise.all(
                                scopeGroups.map(
                                    (scopeGroup: string): Promise<Nullable<GroupMembershipResponse>> =>
                                        getGroupMembers(
                                            telSys,
                                            partInTelSysID,
                                            scopeGroup,
                                            undefined,
                                            undefined,
                                            undefined,
                                            DEVICE_CACHE_TIME
                                        )
                                )
                            )
                                .then(reduceGroupMemberships)
                                .then(
                                    (groupMembers: Nullable<GroupMembershipResponse>): AgentResponse[] =>
                                        (groupMembers?.ACDAgents as AgentResponse[]) ?? ([] as AgentResponse[])
                                )
                                .catch(displayError)
                        }

                        return getAgents(telSys, partInTelSysID, true).catch(displayError)
                    }
                ) ?? []
            ).then((agentsArrays: Voidable<AgentResponse[]>[]): void => {
                setAvailableAgents(
                    [
                        standardAny,
                        ...(
                            agentsArrays.filter(
                                (agentsArray: Voidable<AgentResponse[]>): boolean => !IsVarEmpty(agentsArray)
                            ) as AgentResponse[][]
                        )
                            .reduce(
                                (accum: AgentResponse[], next: AgentResponse[]): AgentResponse[] => [...accum, ...next],
                                []
                            )
                            .map(
                                ({ Description, AgentNumber }: AgentResponse): SelectRangeItem => ({
                                    value: AgentNumber,
                                    description: Description,
                                    renderElement: (
                                        <div>
                                            {AgentNumber} - {Description}
                                        </div>
                                    )
                                })
                            )
                    ].slice(0, MAX_DEVICES_AGENTS)
                )
            })
        } else {
            setAvailableDevices(toEntityOptions(filterDevicesByDeviceTypes))
            setAvailableAgents(toEntityOptions(filterEntityInfo?.EntityInfo.ACDAgents))
        }
    }, [
        filterEntityInfo,
        telSys,
        partitions,
        JSON.stringify(scopeGroups),
        deviceTypes,
        JSON.stringify(filterDevicesByDeviceTypes)
    ])

    return (
        <Fieldset className='locational-filtering'>
            <Heading level={HeadingLevel.ONE}>
                {language.get(LocationalFilteringLanguage.LocationalFiltering_SectionHeader)}
                <HelpTag
                    className='configure-report-help-title-tag'
                    helpContentTitle={HelpTagTitles.CONFIGURE_REPORT_LOCATIONAL_FILTERING}
                    tag={Tags.CONFIGURE_REPORT}
                />
            </Heading>
            <FormRow className={'telephone-numbers'}>
                <TextInput
                    value={telephoneNos}
                    onChange={setTelephoneNos}
                    labelLeft={
                        reportType !== ReportTypes.CALLS_BY_DNIS
                            ? language.get(LocationalFilteringLanguage.TelephonenosLabel)
                            : language.get(LocationalFilteringLanguage.TelephonenosLabelDNIS)
                    }
                    placeholder={language.get(LocationalFilteringLanguage.LocationalFiltering_AnyPlaceholder)}
                />
            </FormRow>
            <FormRow className={'telephone-number-descriptions'}>
                <TextInput
                    value={telephoneNoDescriptions}
                    onChange={setTelephoneNoDescriptions}
                    labelLeft={language.get(LocationalFilteringLanguage.TelephonenoDescriptionsLabel)}
                    placeholder={language.get(LocationalFilteringLanguage.LocationalFiltering_AnyPlaceholder)}
                />
            </FormRow>
            <FormRow className={'devices'}>
                <RangeCombo
                    label={language.get(LocationalFilteringLanguage.LocationalFiltering_DevicesLabel)}
                    options={availableDevices.map(
                        (availableDevice: SelectRangeItem): SelectOption<string> => ({
                            label: availableDevice.description ?? EMPTY_STRING,
                            value: availableDevice.value,
                            renderOptionElement: availableDevice.renderElement
                        })
                    )}
                    allItemIndex={0}
                    originalValue={devices}
                    onChange={(value?: string): void => setDevices(value ?? EMPTY_STRING)}
                    asyncSearchingFunction={
                        isVarNotEmpty(selectedPartitionsPartIDOnTelSys) && selectedPartitionsPartIDOnTelSys.length > 0
                            ? (search: string): Promise<SelectOptions<string>> =>
                                  searchEntitiesAcrossPartitions(
                                      SearchableEntities.DEVICES,
                                      { telSys, partitions: selectedPartitionsPartIDOnTelSys },
                                      search,
                                      deviceTypes,
                                      scopeGroups
                                  )
                            : undefined
                    }
                />
            </FormRow>
            <FormRow className={'acd-agents'}>
                <RangeCombo
                    label={language.getWithTelSysCheck(LocationalFilteringLanguage.AcdagentsLabel, currentTelSysType)}
                    options={availableAgents.map(
                        (availableDevice: SelectRangeItem): SelectOption<string> => ({
                            label: availableDevice.description ?? EMPTY_STRING,
                            value: availableDevice.value,
                            renderOptionElement: availableDevice.renderElement
                        })
                    )}
                    allItemIndex={0}
                    originalValue={ACDAgents}
                    onChange={(value?: string): void => setACDAgents(value ?? EMPTY_STRING)}
                    asyncSearchingFunction={
                        isVarNotEmpty(selectedPartitionsPartIDOnTelSys) && selectedPartitionsPartIDOnTelSys.length > 0
                            ? (search: string): Promise<SelectOptions<string>> =>
                                  searchEntitiesAcrossPartitions(
                                      SearchableEntities.AGENTS,
                                      { telSys, partitions: selectedPartitionsPartIDOnTelSys },
                                      search,
                                      undefined,
                                      scopeGroups
                                  )
                            : undefined
                    }
                />
            </FormRow>
            {isAdvancedMode ? (
                <FormRow className={'location-from-to'}>
                    <Select
                        label={language.get(LocationalFilteringLanguage.LocationLabel)}
                        value={filterLocationToFrom}
                        handleChange={setFilterLocationToFrom}
                        options={locationSelectOptions}
                    />
                </FormRow>
            ) : (
                <></>
            )}
        </Fieldset>
    )
}

export default React.memo(LocationalFiltering)
